import * as React from "react";
import { Form, Field } from "formik";
import { Trans, useTranslation } from "react-i18next";
import { validateField } from "util/FormsUtils";
import { Button, ButtonToolbar, Col, Input } from "components/bootstrap";
import { FormikFormGroup, InputOptionalInfo as Opt, Select, KeyValueTable, TimezoneSelect } from "components/common";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import OidcRoutes from "authentication/components/oidc/Routes";
import getDisplayName from "authentication/helpers/getDisplayName";
import OidcBaseUrlField from "./OidcBaseUrlField";
import payloadFromFormValues from "../helpers/payloadFromFormValues";
import ServerConnectionTest from "../ServerConnectionTest";
const defaultCallbackUrl = `${window.location.origin}${OidcRoutes.AUTH_CALLBACK}`;
const OKTA_EXAMPLE_URL = "https://<your-subdomain>.okta.com";
const OKTA_CUSTOM_AUTHORIZATION_SERVER_URL = "https://<yourOktaDomain>/oauth2/<authorizationServerId>";
const READER_ROLE_NAME = "Reader";
export const FORM_VALIDATION = {
  title: { required: true },
  description: {},
  baseUrl: { required: true },
  callbackUrl: { required: true },
  clientId: { required: true },
  clientSecret: { required: true },
  claims: {},
  defaultRoles: { required: true },
  defaultUserTimezone: {}
};
const ServerConfigurationForm = ({
  authenticationBackend,
  values,
  isSubmitting,
  hasSecret,
  isOkta,
  setFieldValue,
  roles,
  validateForm,
  submitAllError,
  goToNext
}) => {
  const { t } = useTranslation("enterprise_authentication");
  const sendTelemetry = useSendTelemetry();
  const { id } = authenticationBackend;
  const { type } = values;
  const rolesOptions = roles.map((role) => ({ label: role.name, value: role.id }));
  const displayName = getDisplayName(type);
  const oktaBaseUrlHelp = /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "oidc.server_configuration_form.okta_base_url_help",
      components: {
        exampleUrl: /* @__PURE__ */ React.createElement("code", null, OKTA_EXAMPLE_URL),
        customAuthorizationServerUrl: /* @__PURE__ */ React.createElement("code", null, OKTA_CUSTOM_AUTHORIZATION_SERVER_URL)
      }
    }
  );
  const help = {
    title: void 0,
    description: void 0,
    baseUrl: /* @__PURE__ */ React.createElement("span", null, t("oidc.server_configuration_form.base_url_help", { displayName }), " ", isOkta && oktaBaseUrlHelp),
    callbackUrl: /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(
      Trans,
      {
        t,
        i18nKey: "oidc.server_configuration_form.callback_url_help",
        values: { displayName, callbackUrl: defaultCallbackUrl },
        components: { code: /* @__PURE__ */ React.createElement("code", null) }
      }
    )),
    claims: void 0,
    clientId: void 0,
    clientSecret: void 0,
    tokenVerifierConnectTimeout: /* @__PURE__ */ React.createElement("span", null, t("oidc.server_configuration_form.token_verifier_help", { displayName })),
    defaultRoles: /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(
      Trans,
      {
        t,
        i18nKey: "oidc.server_configuration_form.default_roles_help",
        values: { readerRole: READER_ROLE_NAME },
        components: { code: /* @__PURE__ */ React.createElement("code", null) }
      }
    )),
    defaultUserTimezone: /* @__PURE__ */ React.createElement("span", null, t("oidc.server_configuration_form.default_user_timezone_help"))
  };
  const backendValidationErrors = {
    clientSecret: void 0,
    defaultRoles: void 0
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Col, { md: 7 }, /* @__PURE__ */ React.createElement(Form, { className: "form form-horizontal" }, /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      help: help.title,
      label: t("oidc.server_configuration_form.title"),
      name: "title",
      disabled: isSubmitting,
      validate: validateField(FORM_VALIDATION.title),
      placeholder: t("oidc.server_configuration_form.title")
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      help: help.description,
      label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("oidc.server_configuration_form.description"), " ", /* @__PURE__ */ React.createElement(Opt, null)),
      type: "textarea",
      name: "description",
      disabled: isSubmitting,
      validate: validateField(FORM_VALIDATION.description),
      placeholder: t("oidc.server_configuration_form.description")
    }
  ), /* @__PURE__ */ React.createElement(
    OidcBaseUrlField,
    {
      help: help.baseUrl,
      type,
      isOkta,
      disabled: isSubmitting,
      validate: validateField(FORM_VALIDATION.baseUrl)
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      help: help.callbackUrl,
      label: t("oidc.server_configuration_form.callback_url"),
      name: "callbackUrl",
      disabled: isSubmitting,
      validate: validateField(FORM_VALIDATION.callbackUrl),
      placeholder: t("oidc.server_configuration_form.callback_url")
    }
  ), !isOkta && /* @__PURE__ */ React.createElement(Field, { name: "claims" }, ({ meta: { error } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      help: help.claims,
      error,
      id: "claims",
      label: t("oidc.server_configuration_form.claims"),
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      KeyValueTable,
      {
        pairs: values.claims,
        editable: !isSubmitting,
        onChange: (claims) => {
          setFieldValue("claims", claims);
        }
      }
    )
  )), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      help: help.clientId,
      label: t("oidc.server_configuration_form.oauth_client_id"),
      name: "clientId",
      disabled: isSubmitting,
      validate: validateField(FORM_VALIDATION.clientId),
      placeholder: t("oidc.server_configuration_form.oauth_client_id")
    }
  ), hasSecret && values.clientSecret === void 0 ? /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "clientSecret",
      label: t("oidc.server_configuration_form.oauth_client_secret"),
      labelClassName: "col-sm-3",
      disabled: isSubmitting,
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: () => setFieldValue("clientSecret", "") }, t("oidc.server_configuration_form.reset_secret"))
  ) : /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      buttonAfter: hasSecret && values.clientSecret !== void 0 ? /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: () => setFieldValue("clientSecret", void 0) }, t("oidc.server_configuration_form.undo_reset")) : void 0,
      help: help.clientSecret,
      label: t("oidc.server_configuration_form.oauth_client_secret"),
      name: "clientSecret",
      error: backendValidationErrors == null ? void 0 : backendValidationErrors.clientSecret,
      placeholder: t("oidc.server_configuration_form.oauth_client_secret"),
      disabled: isSubmitting,
      validate: validateField(FORM_VALIDATION.clientSecret),
      type: "password"
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      help: help.tokenVerifierConnectTimeout,
      label: t("oidc.server_configuration_form.token_verifier_connect_timeout"),
      type: "number",
      name: "tokenVerifierConnectTimeout",
      disabled: isSubmitting,
      required: true,
      placeholder: t("oidc.server_configuration_form.token_verifier_connect_timeout")
    }
  ), /* @__PURE__ */ React.createElement(Field, { name: "defaultRoles", validate: validateField(FORM_VALIDATION.defaultRoles) }, ({ field: { name, value, onChange, onBlur }, meta: { error } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      bsStyle: error ? "error" : void 0,
      help: help.defaultRoles,
      error: error != null ? error : backendValidationErrors == null ? void 0 : backendValidationErrors.defaultRoles,
      id: "default-roles-select",
      label: t("oidc.server_configuration_form.default_roles"),
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        multi: true,
        onBlur,
        onChange: (selectedRoles) => onChange({ target: { value: selectedRoles, name } }),
        options: rolesOptions,
        placeholder: t("oidc.server_configuration_form.search_for_roles"),
        value
      }
    )
  )), /* @__PURE__ */ React.createElement(Field, { name: "defaultUserTimezone" }, ({ field: { name, value, onChange } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "default-user-timezone-select",
      label: t("oidc.server_configuration_form.default_user_time_zone"),
      help: help.defaultUserTimezone,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      TimezoneSelect,
      {
        value: value || "Browser's time zone",
        name: "timezone",
        onChange: (newValue) => onChange({ target: { name, value: newValue } })
      }
    )
  )), submitAllError, /* @__PURE__ */ React.createElement(ButtonToolbar, { className: "pull-right" }, /* @__PURE__ */ React.createElement(
    Button,
    {
      disabled: isSubmitting,
      onClick: () => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.OIDC_SERVER_CONFIG_SAVE_CLICKED, {
          app_section: "oidc-server-configuration",
          app_action_value: "server-configuration-step"
        });
      },
      type: "submit"
    },
    t("oidc.server_configuration_form.finish_and_save")
  ), /* @__PURE__ */ React.createElement(
    Button,
    {
      bsStyle: "primary",
      disabled: isSubmitting,
      onClick: () => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.OIDC_NEXT_GROUP_SYNC_CLICKED, {
          app_section: "oidc-groupsync-step",
          app_action_value: "group-synchronization-button"
        });
        goToNext();
      },
      type: "button"
    },
    t("oidc.server_configuration_form.next_group_sync")
  )))), /* @__PURE__ */ React.createElement(Col, { md: 5 }, /* @__PURE__ */ React.createElement(
    ServerConnectionTest,
    {
      backendId: id,
      prepareSubmitPayload: () => payloadFromFormValues(values),
      validateForm
    }
  )));
};
export default ServerConfigurationForm;
